import * as THREE from "three";
import { GPU_WORLD_INSTANCE_CAPACITY } from "../core/config";
import { smoothstep } from "../core/math";
import type { WorldItem } from "../core/WorldGenerator";
import { WorldLayer, type ConsumedLookup } from "./WorldLayer";

class BoundedConsumedRegistry implements ConsumedLookup {
  private readonly ids = new Set<number>();

  constructor(private readonly capacity = 4096) {}

  has(id: number): boolean {
    return this.ids.has(id);
  }

  add(id: number): void {
    this.ids.add(id);
    while (this.ids.size > this.capacity) {
      const oldest = this.ids.values().next().value as number | undefined;
      if (oldest === undefined) {
        break;
      }
      this.ids.delete(oldest);
    }
  }

  clear(): void {
    this.ids.clear();
  }

  get size(): number {
    return this.ids.size;
  }
}

export interface WorldMetrics {
  readonly activeInstances: number;
  readonly allocatedSlots: number;
  readonly drawCalls: number;
  readonly layerCount: number;
  readonly consumedHistory: number;
  readonly gpuInstanceCapacity: number;
}

export class WorldManager {
  readonly group = new THREE.Group();

  private readonly consumed = new BoundedConsumedRegistry();
  private activeLayer: WorldLayer;
  private pendingLayer: WorldLayer | null = null;
  private transitionProgress = 0;
  private density: number;

  constructor(
    tierIndex: number,
    density: number,
    playerXMeters: number,
    playerZMeters: number,
  ) {
    this.density = density;
    this.group.name = "streamed-world";
    this.activeLayer = new WorldLayer(
      tierIndex,
      this.consumed,
      density,
      playerXMeters,
      playerZMeters,
    );
    this.group.add(this.activeLayer.group);
  }

  updateStreaming(playerXMeters: number, playerZMeters: number): void {
    this.activeLayer.updateStreaming(playerXMeters, playerZMeters);
    this.pendingLayer?.updateStreaming(playerXMeters, playerZMeters);
  }

  updateMatrices(
    playerXMeters: number,
    playerZMeters: number,
    renderMetersPerUnit: number,
    elapsedSeconds: number,
  ): void {
    this.activeLayer.updateMatrices(
      playerXMeters,
      playerZMeters,
      renderMetersPerUnit,
      elapsedSeconds,
    );
    this.pendingLayer?.updateMatrices(
      playerXMeters,
      playerZMeters,
      renderMetersPerUnit,
      elapsedSeconds,
    );
  }

  get pendingTierIndex(): number | null {
    return this.pendingLayer?.tierIndex ?? null;
  }

  get pendingCompileRoot(): THREE.Object3D | null {
    return this.pendingLayer?.group ?? null;
  }

  get collisionTierIndex(): number {
    return this.collisionLayer.tierIndex;
  }

  prepareTransition(
    targetTierIndex: number,
    playerXMeters: number,
    playerZMeters: number,
  ): void {
    if (
      targetTierIndex === this.activeLayer.tierIndex
      || this.pendingLayer?.tierIndex === targetTierIndex
    ) {
      return;
    }
    if (this.pendingLayer) {
      this.group.remove(this.pendingLayer.group);
      this.pendingLayer.dispose();
    }
    this.pendingLayer = new WorldLayer(
      targetTierIndex,
      this.consumed,
      this.density,
      playerXMeters,
      playerZMeters,
    );
    this.pendingLayer.opacity = 0;
    this.group.add(this.pendingLayer.group);
    this.transitionProgress = 0;
  }

  setPendingCompileVisible(visible: boolean): void {
    if (this.pendingLayer && this.pendingLayer.opacity <= 0.005) {
      this.pendingLayer.group.visible = visible;
    }
  }

  beginTransition(
    targetTierIndex: number,
    playerXMeters: number,
    playerZMeters: number,
  ): void {
    if (targetTierIndex === this.activeLayer.tierIndex) {
      return;
    }
    if (this.pendingLayer?.tierIndex !== targetTierIndex) {
      this.prepareTransition(targetTierIndex, playerXMeters, playerZMeters);
    }
    if (!this.pendingLayer) {
      return;
    }
    this.pendingLayer.group.visible = true;
    this.transitionProgress = 0;
  }

  setTransitionProgress(progress: number): void {
    if (!this.pendingLayer) {
      return;
    }
    this.transitionProgress = Math.max(0, Math.min(1, progress));
    const blend = smoothstep(0, 1, this.transitionProgress);
    this.activeLayer.opacity = 1 - blend;
    this.pendingLayer.opacity = blend;
  }

  finishTransition(): void {
    if (!this.pendingLayer) {
      return;
    }
    const oldLayer = this.activeLayer;
    this.activeLayer = this.pendingLayer;
    this.pendingLayer = null;
    this.activeLayer.opacity = 1;
    this.group.remove(oldLayer.group);
    oldLayer.dispose();
    this.transitionProgress = 0;
  }

  setDensity(density: number, playerXMeters: number, playerZMeters: number): void {
    this.density = density;
    this.activeLayer.setDensity(density, playerXMeters, playerZMeters);
    this.pendingLayer?.setDensity(density, playerXMeters, playerZMeters);
  }

  queryNearby(xMeters: number, zMeters: number, radiusMeters: number): WorldItem[] {
    return this.collisionLayer.queryNearby(xMeters, zMeters, radiusMeters);
  }

  collect(item: WorldItem): void {
    this.collisionLayer.collect(item);
  }

  clearArea(
    tierIndex: number,
    xMeters: number,
    zMeters: number,
    radiusMeters: number,
  ): number {
    let cleared = 0;
    if (this.activeLayer.tierIndex === tierIndex) {
      cleared += this.activeLayer.clearCircle(xMeters, zMeters, radiusMeters);
    }
    if (this.pendingLayer?.tierIndex === tierIndex) {
      cleared += this.pendingLayer.clearCircle(xMeters, zMeters, radiusMeters);
    }
    return cleared;
  }

  findNearestEligible(
    xMeters: number,
    zMeters: number,
    maximumRadiusMeters: number,
  ): WorldItem | null {
    return this.collisionLayer.findNearestEligible(xMeters, zMeters, maximumRadiusMeters);
  }

  findNearestOversized(
    xMeters: number,
    zMeters: number,
    minimumRadiusMeters: number,
  ): WorldItem | null {
    return this.collisionLayer.findNearestOversized(
      xMeters,
      zMeters,
      minimumRadiusMeters,
    );
  }

  metrics(): WorldMetrics {
    const layers = this.pendingLayer ? [this.activeLayer, this.pendingLayer] : [this.activeLayer];
    return {
      activeInstances:
        this.activeLayer.activeCount
        + (this.pendingLayer && this.pendingLayer.opacity > 0.005
          ? this.pendingLayer.activeCount
          : 0),
      allocatedSlots: layers.reduce((sum, layer) => sum + layer.totalSlots, 0),
      drawCalls: layers.reduce((sum, layer) => sum + layer.drawCalls, 0),
      layerCount: layers.length,
      consumedHistory: this.consumed.size,
      gpuInstanceCapacity: layers.length * GPU_WORLD_INSTANCE_CAPACITY,
    };
  }

  reset(
    tierIndex: number,
    playerXMeters: number,
    playerZMeters: number,
  ): void {
    this.group.remove(this.activeLayer.group);
    this.activeLayer.dispose();
    if (this.pendingLayer) {
      this.group.remove(this.pendingLayer.group);
      this.pendingLayer.dispose();
      this.pendingLayer = null;
    }
    this.consumed.clear();
    this.activeLayer = new WorldLayer(
      tierIndex,
      this.consumed,
      this.density,
      playerXMeters,
      playerZMeters,
    );
    this.group.add(this.activeLayer.group);
    this.transitionProgress = 0;
  }

  dispose(): void {
    this.activeLayer.dispose();
    this.pendingLayer?.dispose();
    this.group.clear();
  }

  private get collisionLayer(): WorldLayer {
    if (this.pendingLayer && this.transitionProgress >= 0.45) {
      return this.pendingLayer;
    }
    return this.activeLayer;
  }
}
